import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, isPending, isRejected, isBlocked, logout, loading } = useAuth();

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Verifying credentials...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (isPending) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconCircle}>
            <span style={styles.pendingIcon}>⏳</span>
          </div>
          <h2 style={styles.title}>Approval Pending</h2>
          <p style={styles.message}>
            Your account is currently waiting for administrator approval. 
            Once approved, you will have complete access to all features.
          </p>
          <button style={styles.logoutButton} onClick={logout}>
            Log Out
          </button>
        </div>
      </div>
    );
  }

  if (isRejected) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconCircleRejected}>
            <span style={styles.rejectedIcon}>❌</span>
          </div>
          <h2 style={styles.titleRejected}>Access Denied</h2>
          <p style={styles.message}>
            Your account request was rejected by admin.
          </p>
          <button style={styles.logoutButton} onClick={logout}>
            Log Out
          </button>
        </div>
      </div>
    );
  }

  if (isBlocked) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconCircleBlocked}>
            <span style={styles.blockedIcon}>🚫</span>
          </div>
          <h2 style={styles.titleBlocked}>Account Blocked</h2>
          <p style={styles.message}>
            Your account has been blocked by an administrator. 
            Please contact your Administrator for further queries.
          </p>
          <button style={styles.logoutButton} onClick={logout}>
            Log Out
          </button>
        </div>
      </div>
    );
  }

  return children;
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'radial-gradient(circle, #1a1c24 0%, #0d0f13 100%)',
    fontFamily: "'Outfit', 'Inter', sans-serif",
    padding: '20px',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '40px 30px',
    maxWidth: '450px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
  },
  iconCircle: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(255, 193, 7, 0.1)',
    border: '2px solid #ffc107',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 24px auto',
  },
  iconCircleRejected: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(220, 53, 69, 0.1)',
    border: '2px solid #dc3545',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 24px auto',
  },
  iconCircleBlocked: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '2px solid #ef4444',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 24px auto',
  },
  pendingIcon: {
    fontSize: '36px',
  },
  rejectedIcon: {
    fontSize: '36px',
  },
  blockedIcon: {
    fontSize: '36px',
  },
  title: {
    color: '#ffc107',
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '16px',
  },
  titleRejected: {
    color: '#dc3545',
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '16px',
  },
  titleBlocked: {
    color: '#ef4444',
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '16px',
  },
  message: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '30px',
  },
  logoutButton: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 30px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#0d0f13',
    fontFamily: "'Outfit', 'Inter', sans-serif",
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '3px solid rgba(59, 130, 246, 0.1)',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: '20px',
    fontSize: '16px',
    fontWeight: '500',
  },
};

// Add standard keyframe spin for loading spinner
const styleSheet = document.styleSheets[0];
if (styleSheet) {
  try {
    styleSheet.insertRule(`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `, styleSheet.cssRules.length);
  } catch (e) {}
}

export default PrivateRoute;